import React, { useState, useEffect } from "react";
import { BaseUrl } from "../config/api";
import "../assets/styles/MyAccount.css";

const MyAccount = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchEmail, setSearchEmail] = useState("");
  const [searchedEmail, setSearchedEmail] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);

  const fetchOrders = async (email) => {
    if (!email) {
      setError("Please enter your email address");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${BaseUrl}/orders`);
      if (!response.ok) {
        throw new Error("Failed to fetch orders");
      }
      const data = await response.json();

      // Filter orders by email
      const userOrders = data.filter(
        (order) =>
          order.shippingAddress?.email?.toLowerCase() === email.toLowerCase()
      );

      // Sort by date (newest first)
      userOrders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setOrders(userOrders);
      setSearchedEmail(email);
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError("Failed to load orders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchOrders(searchEmail);
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-AU", options);
  };

  const formatPrice = (price) => {
    return parseFloat(price || 0).toFixed(2);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered":
        return "#28a745";
      case "shipped":
        return "#17a2b8";
      case "packed":
        return "#ffc107";
      case "processing":
        return "#6c757d";
      case "hold":
        return "#dc3545";
      default:
        return "#6c757d";
    }
  };

  const getPaymentStatusColor = (isPaid) => {
    return isPaid ? "#28a745" : "#dc3545";
  };

  return (
    <div className="my-account-container">
      <h1 className="my-account-title">My Account</h1>

      <div className="order-search-section">
        <h2>Track Your Orders</h2>
        <p>Enter your email address to view your order history</p>
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="email"
            placeholder="Enter your email address"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            className="email-input"
            required
          />
          <button type="submit" className="search-btn">
            View Orders
          </button>
        </form>
      </div>

      {loading && searchedEmail && (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading your orders...</p>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {!loading && searchedEmail && orders.length === 0 && (
        <div className="no-orders">
          <p>No orders found for {searchedEmail}</p>
        </div>
      )}

      {!loading && orders.length > 0 && (
        <div className="orders-section">
          <h2>Order History ({orders.length} orders)</h2>

          <div className="orders-list">
            {orders.map((order) => (
              <div key={order._id} className="order-card">
                <div className="order-header">
                  <div className="order-info">
                    <span className="order-id">Order #{order._id.slice(-8).toUpperCase()}</span>
                    <span className="order-date">{formatDate(order.createdAt)}</span>
                  </div>
                  <div className="order-status-badges">
                    <span
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(order.shippingStatus) }}
                    >
                      {order.shippingStatus || "Processing"}
                    </span>
                    <span
                      className="payment-badge"
                      style={{ backgroundColor: getPaymentStatusColor(order.isPaid) }}
                    >
                      {order.isPaid ? "Paid" : "Pending"}
                    </span>
                  </div>
                </div>

                <div className="order-items">
                  {order.orderItems?.map((item) => (
                    <div key={item._id} className="order-item">
                      <img
                        src={item.image || "/placeholder.jpg"}
                        alt={item.name}
                        className="item-image"
                        onError={(e) => {
                          e.target.src = "/placeholder.jpg";
                        }}
                      />
                      <div className="item-details">
                        <span className="item-name">{item.name}</span>
                        <span className="item-qty">Qty: {item.qty}</span>
                        <span className="item-price">${formatPrice(item.price)}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="order-footer">
                  <div className="order-address">
                    <strong>Shipping to:</strong>
                    <p>
                      {order.shippingAddress?.firstName} {order.shippingAddress?.lastName}
                      <br />
                      {order.shippingAddress?.addressLine1}
                      {order.shippingAddress?.addressLine2 && (
                        <>, {order.shippingAddress.addressLine2}</>
                      )}
                      <br />
                      {order.shippingAddress?.city}, {order.shippingAddress?.state}{" "}
                      {order.shippingAddress?.pincode}
                    </p>
                  </div>
                  <div className="order-total">
                    <div className="total-row">
                      <span>Subtotal:</span>
                      <span>${formatPrice(order.itemsPrice)}</span>
                    </div>
                    <div className="total-row">
                      <span>Shipping:</span>
                      <span>${formatPrice(order.shippingPrice)}</span>
                    </div>
                    <div className="total-row">
                      <span>Tax:</span>
                      <span>${formatPrice(order.taxPrice)}</span>
                    </div>
                    <div className="total-row grand-total">
                      <span>Total:</span>
                      <span>${formatPrice(order.totalPrice)}</span>
                    </div>
                    <div className="payment-method">
                      <span>Payment: </span>
                      <span className="method">{order.paymentMethod?.toUpperCase()}</span>
                    </div>
                  </div>
                </div>

                {order.isDelivered && (
                  <div className="delivered-info">
                    Delivered on {formatDate(order.deliveredAt)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAccount;
